import {
  VariantCreateRequestApiV2Dto,
  VariantUpdateRequestApiV2Dto,
  VariantDeleteRequestApiV2Dto,
  VariantRequestDto,
} from '@tiendanube/common';
import { LanguagesType } from 'App/i18n';
import { Variant } from 'domains/Catalog/Products/pages/components/Variants/types';
import { getAttributes } from './helpers';

type VariantRequestV2Dto =
  | VariantCreateRequestApiV2Dto
  | VariantUpdateRequestApiV2Dto
  | VariantDeleteRequestApiV2Dto;

const getVariantToCreate = (
  newVariant: VariantRequestDto,
  language: LanguagesType,
): VariantCreateRequestApiV2Dto => ({
  price: newVariant.price ?? 0,
  inventory_levels: [
    { location_id: newVariant.locationId ?? '', stock: newVariant.stock ?? 0 },
  ],
  attributes: newVariant.values.map((value) => ({
    ...value,
    default: value[language],
  })),
  weight: Number(newVariant.weight) || 0,
  width: Number(newVariant.width) || 0,
  height: Number(newVariant.height) || 0,
  depth: Number(newVariant.depth) || 0,
});

// TODO:
// This function contains logic that's pretty similar, if not the same
// as the one in generateProductPayload.ts. As well as skipping the rule
// of max-statements.
// This can be refactored to use the same logic for both functions but
// for the sake of clarity, I'll keep them separate for now
// until both Variant and Product entities contain all the fields
// and the corresponding types are completed.
// eslint-disable-next-line max-statements
const getVariantToUpdate = (
  originalVariant: Variant,
  newVariant: VariantRequestDto,
  language: LanguagesType,
): VariantUpdateRequestApiV2Dto => {
  const variantToUpdate: Partial<VariantUpdateRequestApiV2Dto> = {
    id: Number(newVariant.id),
  };

  if (
    newVariant.price &&
    Number(originalVariant.price) !== Number(newVariant.price)
  ) {
    Object.assign(variantToUpdate, {
      price: newVariant.price,
    });
  }

  if (
    newVariant.locationId &&
    originalVariant.locationId !== newVariant.locationId
  ) {
    Object.assign(variantToUpdate, {
      inventory_levels: [
        {
          location_id: newVariant.locationId || '',
          stock: newVariant.stock || 0,
        },
      ],
    });
  }

  if (newVariant.weight && originalVariant.weight !== newVariant.weight) {
    Object.assign(variantToUpdate, {
      weight: Number(newVariant.weight),
    });
  }

  if (newVariant.width && originalVariant.width !== newVariant.width) {
    Object.assign(variantToUpdate, {
      width: Number(newVariant.width),
    });
  }

  if (newVariant.height && originalVariant.height !== newVariant.height) {
    Object.assign(variantToUpdate, {
      height: Number(newVariant.height),
    });
  }

  if (newVariant.depth && originalVariant.depth !== newVariant.depth) {
    Object.assign(variantToUpdate, {
      depth: Number(newVariant.depth),
    });
  }

  if (newVariant.values) {
    Object.assign(variantToUpdate, {
      attributes: getAttributes(language, newVariant.values),
    });
  }

  return {
    ...variantToUpdate,
  } as VariantUpdateRequestApiV2Dto;
};

export const generateVariantsPayload = (
  language: LanguagesType,
  originalVariants: Variant[],
  newVariants: VariantRequestDto[] | undefined,
): VariantRequestV2Dto[] => {
  if (!newVariants) return [];

  const variantsToCreate: VariantCreateRequestApiV2Dto[] = [];
  const variantsToUpdate: VariantUpdateRequestApiV2Dto[] = [];
  const variantsToDelete: VariantDeleteRequestApiV2Dto[] = [];

  newVariants.forEach((variant) => {
    if (!variant.id) {
      const variantToCreate = getVariantToCreate(variant, language);
      variantsToCreate.push(variantToCreate);
    } else {
      // TODO: optimize this
      const originalVariant = originalVariants.find((v) => v.id === variant.id);
      if (!originalVariant) return;

      const variantToUpdate = getVariantToUpdate(
        originalVariant,
        variant,
        language,
      );
      variantsToUpdate.push(variantToUpdate);
    }
  });

  originalVariants.forEach((variant) => {
    if (!newVariants.find((v) => v.id === variant.id)) {
      variantsToDelete.push({ id: Number(variant.id), '@delete': true });
    }
  });

  return [...variantsToCreate, ...variantsToUpdate, ...variantsToDelete];
};
